<mat-toolbar color="primary">
  <h1>File Integrity Dashboard</h1>
</mat-toolbar>

<div style="padding: 16px; max-width: 1400px; margin: 0 auto;">
  <p style="text-align: center; color: #666; margin-bottom: 16px;">Monitor the integrity of critical system files across all applications</p>

  <!-- Application Filter -->
  <mat-card style="margin-bottom: 16px;">
    <mat-card-content style="display: flex; align-items: center; gap: 16px; justify-content: space-between;">
      <mat-form-field appearance="outline" style="flex: 1; max-width: 400px;">
        <mat-label>Filter by Application</mat-label>
        <mat-select [(ngModel)]="selected_application_id" (selectionChange)="onApplicationChange()">
          <mat-option [value]="null">All Applications</mat-option>
          <mat-option *ngFor="let app of applications" [value]="app.Value">
            {{ app.Name }}
          </mat-option>
        </mat-select>
      </mat-form-field>

      <button mat-raised-button color="primary" (click)="checkAllApplicationsFileIntegrity()" [disabled]="checking">
        <mat-icon *ngIf="!checking">fact_check</mat-icon>
        <mat-progress-spinner *ngIf="checking" diameter="20" mode="indeterminate"></mat-progress-spinner>
        {{ checking ? 'Verifying...' : 'Rerun Verification' }}
      </button>
    </mat-card-content>
  </mat-card>

  <mat-card *ngIf="error_message" style="margin-bottom: 16px; background-color: #ffebee; border-left: 4px solid #f44336;">
    <mat-card-content style="display: flex; align-items: center; gap: 10px; color: #f44336;">
      <mat-icon>error</mat-icon>
      <span>{{ error_message }}</span>
    </mat-card-content>
  </mat-card>

  <div *ngIf="loading" style="display: flex; flex-direction: column; align-items: center; padding: 40px; gap: 20px;">
    <mat-progress-spinner mode="indeterminate"></mat-progress-spinner>
    <p>Loading file integrity records...</p>
  </div>

  <div *ngIf="!loading">

    <!-- Status Card -->
    <mat-card style="margin-bottom: 24px;">
      <mat-card-header>
        <mat-icon mat-card-avatar [style.color]="statusColor">{{ statusIcon }}</mat-icon>
        <mat-card-title>{{ statusTitle }}</mat-card-title>
        <mat-card-subtitle [style.color]="statusColor">{{ statusText }}</mat-card-subtitle>
      </mat-card-header>
      <mat-card-content>
        <!-- Single Application Check Stats -->
        <mat-chip-listbox *ngIf="check_mode === FileIntegrityCheckModeType.Single && check_result && !check_error" style="margin-bottom: 16px;">
          <mat-chip-option>Total Files: {{ check_result.TotalFilesChecked }}</mat-chip-option>
          <mat-chip-option style="background: #e8f5e9;">Valid: {{ validFilesCount }}</mat-chip-option>
          <mat-chip-option style="background: #fff3e0;">Mismatches: {{ check_result.Mismatches.length }}</mat-chip-option>
          <mat-chip-option style="background: #ffebee;">Errors: {{ check_result.Errors.length }}</mat-chip-option>
        </mat-chip-listbox>

        <!-- Multi Application Check Stats -->
        <mat-chip-listbox *ngIf="check_mode === FileIntegrityCheckModeType.Multi && multi_app_check_result && !check_error" style="margin-bottom: 16px;">
          <mat-chip-option *ngIf="!selected_application_id">Applications: {{ filteredApplicationsCount }}</mat-chip-option>
          <mat-chip-option>Total Files: {{ totalFilesMonitored }}</mat-chip-option>
          <mat-chip-option style="background: #e8f5e9;">Valid: {{ filteredValidFilesCount }}</mat-chip-option>
          <mat-chip-option style="background: #fff3e0;">Mismatches: {{ filteredMismatchesCount }}</mat-chip-option>
          <mat-chip-option style="background: #ffebee;">Errors: {{ filteredErrorsCount }}</mat-chip-option>
        </mat-chip-listbox>

        <!-- Error Stats -->
        <mat-chip-listbox *ngIf="check_error" style="margin-bottom: 16px;">
          <mat-chip-option style="background: #ffebee; color: #f44336;">Status: Check Failed</mat-chip-option>
          <mat-chip-option style="background: #ffebee;">Last Attempt: {{ last_check_attempted ? 'Failed' : 'Never' }}</mat-chip-option>
        </mat-chip-listbox>

        <!-- Last Checked Info -->
        <p *ngIf="check_mode === FileIntegrityCheckModeType.Single && check_result && !check_error" style="margin: 8px 0; color: #666;">
          Last checked: {{ check_result.CheckedAt | date:'short' }} | Server: {{ check_result.ServerIpAddress }}
        </p>
        <p *ngIf="check_mode === FileIntegrityCheckModeType.Multi && multi_app_check_result && !check_error" style="margin: 8px 0; color: #666;">
          Last checked: {{ multi_app_check_result.CheckedAt | date:'short' }}
        </p>
        <p *ngIf="check_error" style="margin: 8px 0;">Last check attempt failed. Click "Retry Check" to try again.</p>
      </mat-card-content>
      <mat-card-actions *ngIf="check_error">
        <button mat-raised-button color="warn" (click)="retryCheck()" [disabled]="checking">
          <mat-icon *ngIf="!checking">refresh</mat-icon>
          <mat-progress-spinner *ngIf="checking" diameter="20" mode="indeterminate"></mat-progress-spinner>
          {{ checking ? 'Retrying...' : 'Retry Check' }}
        </button>
      </mat-card-actions>
    </mat-card>

    <mat-tab-group>
      <mat-tab label="Overview">
        <mat-card style="margin: 16px;">
          <mat-card-header>
            <mat-card-title>File Integrity Overview</mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <mat-grid-list cols="3" rowHeight="120px" gutterSize="16px">
              <mat-grid-tile>
                <div style="display: flex; flex-direction: column; align-items: center; text-align: center;">
                  <mat-icon color="primary" style="font-size: 48px; width: 48px; height: 48px;">folder</mat-icon>
                  <h3 style="margin: 8px 0;">Total Files Monitored</h3>
                  <p style="font-size: 1.5rem; font-weight: bold; margin: 0;">{{ totalFilesMonitored }}</p>
                </div>
              </mat-grid-tile>
              <mat-grid-tile>
                <div style="display: flex; flex-direction: column; align-items: center; text-align: center;">
                  <mat-icon color="accent" style="font-size: 48px; width: 48px; height: 48px;">security</mat-icon>
                  <h3 style="margin: 8px 0;">Security Status</h3>
                  <p [style.color]="statusColor" style="font-size: 1.5rem; font-weight: bold; margin: 0;">{{ statusText }}</p>
                </div>
              </mat-grid-tile>
              <mat-grid-tile>
                <div style="display: flex; flex-direction: column; align-items: center; text-align: center;">
                  <mat-icon color="warn" style="font-size: 48px; width: 48px; height: 48px;">schedule</mat-icon>
                  <h3 style="margin: 8px 0;">Last Check</h3>
                  <p *ngIf="(check_result || multi_app_check_result) && !check_error" style="margin: 0;">
                    {{ (check_result?.CheckedAt || multi_app_check_result?.CheckedAt) | date:'short' }}
                  </p>
                  <p *ngIf="check_error" style="color: #f44336; margin: 0;">Failed</p>
                  <p *ngIf="!check_result && !multi_app_check_result && !check_error" style="margin: 0;">Never</p>
                </div>
              </mat-grid-tile>
            </mat-grid-list>
          </mat-card-content>
        </mat-card>
      </mat-tab>

      <!-- Multi-Application Results Tab -->
      <mat-tab label="Applications" *ngIf="check_mode === FileIntegrityCheckModeType.Multi && multi_app_check_result">
        <mat-card style="margin: 16px;">
          <mat-card-header>
            <mat-card-title>Application Results</mat-card-title>
            <mat-card-subtitle>{{ multi_app_check_result.TotalApplications }} applications checked</mat-card-subtitle>
          </mat-card-header>
          <mat-card-content>
            <mat-accordion>
              <mat-expansion-panel *ngFor="let appResult of multi_app_check_result.ApplicationTypeResults">
                <mat-expansion-panel-header>
                  <mat-panel-title>
                    <mat-icon [style.color]="appResult.IsSuccess ? 'green' : (appResult.Mismatches.length > 0 ? 'orange' : 'red')">
                      {{ appResult.IsSuccess ? 'check_circle' : (appResult.Mismatches.length > 0 ? 'warning' : 'error') }}
                    </mat-icon>
                    <span style="margin-left: 8px;">{{ appResult.ApplicationName }}</span>
                  </mat-panel-title>
                  <mat-panel-description>
                    Files: {{ appResult.TotalFilesChecked }} | Mismatches: {{ appResult.Mismatches.length }} | Errors: {{ appResult.Errors.length }}
                  </mat-panel-description>
                </mat-expansion-panel-header>

                <div style="padding: 16px;">
                  <p><strong>Server:</strong> {{ appResult.ServerIpAddress }}</p>
                  <p><strong>Checked:</strong> {{ appResult.CheckedAt | date:'short' }}</p>
                  <p *ngIf="appResult.ErrorMessage" style="color: #f44336;"><strong>Error:</strong> {{ appResult.ErrorMessage }}</p>

                  <mat-divider *ngIf="appResult.Mismatches.length > 0" style="margin: 16px 0;"></mat-divider>
                  <div *ngIf="appResult.Mismatches.length > 0">
                    <h4>Mismatches ({{ appResult.Mismatches.length }})</h4>
                    <mat-list>
                      <mat-list-item *ngFor="let mismatch of appResult.Mismatches" style="background: #fff3e0; margin-bottom: 8px; border-left: 3px solid #ff9800;">
                        <mat-icon matListItemIcon color="warn">warning</mat-icon>
                        <div matListItemTitle><strong>{{ mismatch.Name }}</strong></div>
                        <div matListItemLine><strong>Path:</strong> {{ mismatch.FilePath }}</div>
                        <div matListItemLine><strong>Expected:</strong> <code style="font-size: 0.85em;">{{ mismatch.ExpectedChecksum }}</code></div>
                        <div matListItemLine><strong>Actual:</strong> <code style="font-size: 0.85em;">{{ mismatch.ActualChecksum }}</code></div>
                      </mat-list-item>
                    </mat-list>
                  </div>

                  <mat-divider *ngIf="appResult.Errors.length > 0" style="margin: 16px 0;"></mat-divider>
                  <div *ngIf="appResult.Errors.length > 0">
                    <h4>Errors ({{ appResult.Errors.length }})</h4>
                    <mat-list>
                      <mat-list-item *ngFor="let error of appResult.Errors" style="background: #ffebee; margin-bottom: 8px; border-left: 3px solid #f44336;">
                        <mat-icon matListItemIcon color="warn">error</mat-icon>
                        <div matListItemTitle><strong>{{ error.Name }}</strong></div>
                        <div matListItemLine><strong>Path:</strong> {{ error.FilePath }}</div>
                        <div matListItemLine><strong>Error:</strong> {{ error.ErrorMessage }}</div>
                      </mat-list-item>
                    </mat-list>
                  </div>
                </div>
              </mat-expansion-panel>
            </mat-accordion>
          </mat-card-content>
        </mat-card>
      </mat-tab>

      <mat-tab label="All Files">
        <mat-card style="margin: 16px;">
          <mat-card-header>
            <mat-card-title>All Monitored Files</mat-card-title>
            <mat-card-subtitle *ngIf="selected_application_id">Filtered by: {{ selectedApplicationName }}</mat-card-subtitle>
          </mat-card-header>
          <mat-card-content>
            <app-table-export-buttons
              testIdPrefix="file-integrity"
              [disabled]="!file_integrity_data_source.data.length"
              (exportCsv)="onExportRegisterCsv()"
              (exportPdf)="onExportRegisterPdf()">
            </app-table-export-buttons>

            <table mat-table [dataSource]="file_integrity_data_source" class="mat-elevation-z2" style="width: 100%;">
              <ng-container matColumnDef="name">
                <th mat-header-cell *matHeaderCellDef>File Name</th>
                <td mat-cell *matCellDef="let file">{{ file.Name }}</td>
              </ng-container>
              <ng-container matColumnDef="path">
                <th mat-header-cell *matHeaderCellDef>File Path</th>
                <td mat-cell *matCellDef="let file" [matTooltip]="file.FilePath" matTooltipPosition="above" style="font-family: monospace; font-size: 0.9em;">{{ file.FilePath }}</td>
              </ng-container>
              <ng-container matColumnDef="checksum">
                <th mat-header-cell *matHeaderCellDef>Checksum</th>
                <td mat-cell *matCellDef="let file" [matTooltip]="file.CurrentChecksum" matTooltipPosition="above" style="font-family: monospace; font-size: 0.9em;">{{ file.CurrentChecksum | truncateChecksum }}</td>
              </ng-container>
              <ng-container matColumnDef="updated">
                <th mat-header-cell *matHeaderCellDef>Last Updated</th>
                <td mat-cell *matCellDef="let file">{{ file.UpdatedOn | date:'short' }}</td>
              </ng-container>
              <ng-container matColumnDef="status">
                <th mat-header-cell *matHeaderCellDef>Status</th>
                <td mat-cell *matCellDef="let file">
                  <mat-icon [style.color]="fileStatusColors.get(file.Id) || 'gray'">{{ fileStatusIcons.get(file.Id) || 'help' }}</mat-icon>
                </td>
              </ng-container>
              <tr mat-header-row *matHeaderRowDef="['name', 'path', 'checksum', 'updated', 'status']"></tr>
              <tr mat-row *matRowDef="let row; columns: ['name', 'path', 'checksum', 'updated', 'status'];"></tr>
            </table>
          </mat-card-content>
        </mat-card>
      </mat-tab>

      <mat-tab label="Mismatches" [disabled]="allMismatches.length === 0">
        <mat-card style="margin: 16px;">
          <mat-card-header>
            <mat-card-title>File Mismatches</mat-card-title>
            <mat-card-subtitle>{{ allMismatches.length }} files with checksum mismatches</mat-card-subtitle>
          </mat-card-header>
          <mat-card-content>
            <mat-list *ngIf="allMismatches.length > 0">
              <mat-list-item *ngFor="let mismatch of allMismatches" style="background: #fff3e0; margin-bottom: 8px; border-left: 3px solid #ff9800;">
                <mat-icon matListItemIcon color="warn">warning</mat-icon>
                <div matListItemTitle><strong>{{ mismatch.Name }}</strong></div>
                <div matListItemLine><strong>Path:</strong> {{ mismatch.FilePath }}</div>
                <div matListItemLine><strong>Expected:</strong> <code style="font-size: 0.85em;">{{ mismatch.ExpectedChecksum }}</code></div>
                <div matListItemLine><strong>Actual:</strong> <code style="font-size: 0.85em;">{{ mismatch.ActualChecksum }}</code></div>
              </mat-list-item>
            </mat-list>
          </mat-card-content>
        </mat-card>
      </mat-tab>

      <mat-tab label="Errors" [disabled]="allErrors.length === 0">
        <mat-card style="margin: 16px;">
          <mat-card-header>
            <mat-card-title>File Errors</mat-card-title>
            <mat-card-subtitle>{{ allErrors.length }} files with errors</mat-card-subtitle>
          </mat-card-header>
          <mat-card-content>
            <mat-list *ngIf="allErrors.length > 0">
              <mat-list-item *ngFor="let error of allErrors" style="background: #ffebee; margin-bottom: 8px; border-left: 3px solid #f44336;">
                <mat-icon matListItemIcon color="warn">error</mat-icon>
                <div matListItemTitle><strong>{{ error.Name }}</strong></div>
                <div matListItemLine><strong>Path:</strong> {{ error.FilePath }}</div>
                <div matListItemLine><strong>Error:</strong> {{ error.ErrorMessage }}</div>
              </mat-list-item>
            </mat-list>
          </mat-card-content>
        </mat-card>
      </mat-tab>
    </mat-tab-group>
  </div>
</div>
